import moment from 'moment-timezone'
import { formatCurrency } from '@smarttransit/common'
import { addAlert, isAuthorized } from '@/utilities/helpers'
import { getRecordsByDataProperty } from '@/services/reports-service'
import { findTransportationProfiles } from '@/services/transportation-profiles-service'

export default {
  name: 'dashboard',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      summaryLoading: false,
      summaryCards: [],
      ticketRevenueSummaryCards: [],
      vehicleNameById: {},
      vehicleAgencyById: {},
      countryCurrenciesByCountryId: {}
    }
  },
  computed: {
    getSignedInUser () {
      return this.$store.state.credentials.user
    }
  },
  mounted () {
    if (this.getSignedInUser) {
      this.loadDashboardSummary()
    }
  },
  watch: {
    getSignedInUser (updatedUser, oldUser) {
      if (updatedUser && (!oldUser || oldUser.id !== updatedUser.id)) {
        this.loadDashboardSummary()
      }
    }
  },
  methods: {
    afterLoginForward (signInResult) {
      if (this.$route.query.forward) {
        this.$router.push({ path: this.$route.query.forward })
      }
    },
    async loadDashboardSummary () {
      if (!this.getSignedInUser) {
        this.summaryCards = []
        this.ticketRevenueSummaryCards = []
        return
      }

      this.summaryLoading = true
      this.summaryCards = []
      this.ticketRevenueSummaryCards = []

      try {
        const scopedVehicleIds = await this.resolveScopedVehicleIds()
        const periodConfigs = this.buildSummaryPeriods()
        this.countryCurrenciesByCountryId = await this.$store.getters.getCountryCurrencies || {}

        const results = await Promise.all([
          Promise.all(periodConfigs.map(async (periodConfig) => {
            const records = await this.findRecordsForPeriodByDataProperty(periodConfig, scopedVehicleIds, {
              reportType: 'distance_travelled',
              dataProperty: 'distance'
            })
            return this.buildDistanceSummaryCard(periodConfig, records)
          })),
          Promise.all(periodConfigs.map(async (periodConfig) => {
            const records = await this.findRecordsForPeriodByDataProperty(periodConfig, scopedVehicleIds, {
              reportType: 'ticket_revenue',
              dataProperty: 'totalAmount'
            })
            return this.buildTicketRevenueSummaryCard(periodConfig, records)
          }))
        ])

        this.summaryCards = results[0]
        this.ticketRevenueSummaryCards = results[1]
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.summaryLoading = false
      }
    },
    buildSummaryPeriods () {
      const dayStart = moment.utc().startOf('day').subtract(1, 'day')
      const dayEnd = moment.utc().startOf('day')

      const weekStart = moment.utc().startOf('isoWeek').subtract(1, 'week')
      const weekEnd = moment.utc().startOf('isoWeek')

      const monthStart = moment.utc().startOf('month').subtract(1, 'month')
      const monthEnd = moment.utc().startOf('month')

      const yearStart = moment.utc().startOf('year').subtract(1, 'year')
      const yearEnd = moment.utc().startOf('year')

      return [
        {
          key: 'day',
          title: `Yesterday - ${dayStart.format('ddd')}`,
          sourceType: 'source',
          reportFidelity: null,
          periodStartIso: dayStart.toISOString(),
          periodEndIso: dayEnd.toISOString()
        },
        {
          key: 'week',
          title: 'Last Week',
          sourceType: 'aggregate',
          reportFidelity: 'week',
          periodStartIso: weekStart.toISOString(),
          periodEndIso: weekEnd.toISOString()
        },
        {
          key: 'month',
          title: `Last Month - ${monthStart.format('MMM')}`,
          sourceType: 'aggregate',
          reportFidelity: 'month',
          periodStartIso: monthStart.toISOString(),
          periodEndIso: monthEnd.toISOString()
        },
        {
          key: 'year',
          title: `Last Year - ${yearStart.format('YYYY')}`,
          sourceType: 'aggregate',
          reportFidelity: 'year',
          periodStartIso: yearStart.toISOString(),
          periodEndIso: yearEnd.toISOString()
        }
      ]
    },
    async resolveScopedVehicleIds () {
      const user = this.getSignedInUser
      const isStaff = isAuthorized(user, 'staff')
      const isTransporter = isAuthorized(user, 'transporter')

      if (!isTransporter || isStaff) {
        return null
      }

      const transportationOwnerUsers = user && user.transportationOwnerUsers ? user.transportationOwnerUsers : []
      const ownerIds = transportationOwnerUsers
        .map((item) => item && item.transportationOwnerId)
        .filter((id) => !!id)

      if (!ownerIds.length) {
        return []
      }

      const ownerWhere = ownerIds.length === 1
        ? { transportationOwnerId: ownerIds[0] }
        : { transportationOwnerId: { inq: ownerIds } }

      const profiles = await findTransportationProfiles({
        fields: { id: true, licensePlateNumber: true, agencyId: true },
        join: {
          relation: 'transportationOwnerProfile',
          scope: { where: ownerWhere }
        }
      })

      const ids = []
      for (let i = 0; i < profiles.length; i += 1) {
        const profile = profiles[i]
        if (profile && profile.id) {
          ids.push(profile.id)
          this.vehicleNameById[profile.id] = profile.licensePlateNumber || profile.id
          if (profile.agencyId) {
            this.vehicleAgencyById[profile.id] = String(profile.agencyId)
          }
        }
      }

      return ids
    },
    async findRecordsForPeriodByDataProperty (periodConfig, scopedVehicleIds, {
      reportType,
      dataProperty
    } = {}) {
      if (Array.isArray(scopedVehicleIds) && !scopedVehicleIds.length) {
        return []
      }

      const scopedCategoryIds = Array.isArray(scopedVehicleIds) && scopedVehicleIds.length
        ? scopedVehicleIds
        : undefined

      const records = await getRecordsByDataProperty({
        target: periodConfig.sourceType === 'source' ? 'source' : 'aggregate',
        reportType: reportType,
        sortOrder: 'DESC',
        propertyType: 'numeric',
        periodStart: periodConfig.periodStartIso,
        periodEnd: periodConfig.periodEndIso,
        dataProperty: dataProperty,
        categoryIds: scopedCategoryIds,
        limit: 5,
        offset: 0
      })

      const safeRecords = Array.isArray(records) ? records : []

      return safeRecords.filter((record) => {
        const isCompleted = record && record.reportStatus === 'completed'
        const isTransportationCategory = record && record.category === 'transportation'

        if (!Array.isArray(scopedVehicleIds) || !scopedVehicleIds.length) {
          return isCompleted && isTransportationCategory
        }

        return isCompleted &&
          isTransportationCategory &&
          !!(record && record.categoryId) &&
          scopedVehicleIds.includes(String(record.categoryId || ''))
      })
    },
    async buildDistanceSummaryCard (periodConfig, records) {
      const distanceByVehicleId = {}

      for (let i = 0; i < records.length; i += 1) {
        const record = records[i]
        const vehicleId = record && record.categoryId ? String(record.categoryId) : null
        if (!vehicleId) {
          continue
        }
        const distance = record && record.data && record.data.distance ? Number(record.data.distance) : 0
        if (!distanceByVehicleId[vehicleId]) {
          distanceByVehicleId[vehicleId] = 0
        }
        distanceByVehicleId[vehicleId] += Number.isFinite(distance) ? distance : 0
      }

      const ranking = Object.keys(distanceByVehicleId).map((vehicleId) => ({
        vehicleId: vehicleId,
        distance: Number(distanceByVehicleId[vehicleId] || 0)
      })).sort((a, b) => b.distance - a.distance)

      await this.ensureVehicleLabels(ranking.map((item) => item.vehicleId))

      const topRecord = ranking.length ? ranking[0] : null
      const rankedVehicles = ranking.slice(0, 5)

      return {
        key: periodConfig.key,
        title: periodConfig.title,
        hasData: !!topRecord,
        rankedVehicles: rankedVehicles.map((item) => ({
          vehicleId: item.vehicleId,
          vehicleLabel: this.vehicleLabel(item.vehicleId),
          distanceLabel: this.formatDistance(item.distance)
        }))
      }
    },
    async buildTicketRevenueSummaryCard (periodConfig, records) {
      const revenueByVehicleId = {}

      for (let i = 0; i < records.length; i += 1) {
        const record = records[i]
        const vehicleId = record && record.categoryId ? String(record.categoryId) : null
        if (!vehicleId) {
          continue
        }

        const data = record && record.data ? record.data : {}
        let revenueAmount = 0
        if (data.totalAmount !== null && data.totalAmount !== undefined) {
          revenueAmount = Number(data.totalAmount)
        } else if (data.totalTicketAmount !== null && data.totalTicketAmount !== undefined) {
          revenueAmount = Number(data.totalTicketAmount)
        }

        if (!revenueByVehicleId[vehicleId]) {
          revenueByVehicleId[vehicleId] = 0
        }
        revenueByVehicleId[vehicleId] += Number.isFinite(revenueAmount) ? revenueAmount : 0
      }

      const ranking = Object.keys(revenueByVehicleId).map((vehicleId) => ({
        vehicleId: vehicleId,
        revenueAmount: Number(revenueByVehicleId[vehicleId] || 0)
      })).sort((a, b) => b.revenueAmount - a.revenueAmount)

      await this.ensureVehicleLabels(ranking.map((item) => item.vehicleId))

      const rankedVehicles = ranking.slice(0, 5)
      const chartVehicles = rankedVehicles.slice().reverse()
      const hasData = rankedVehicles.some((item) => item.revenueAmount > 0)

      return {
        key: periodConfig.key,
        title: periodConfig.title,
        hasData: hasData,
        currencySymbol: this.resolveCurrencySymbolForVehicleIds(rankedVehicles.map((item) => item.vehicleId)),
        vehicleLabels: chartVehicles.map((item) => this.vehicleLabel(item.vehicleId)),
        series: [{
          name: 'Ticket Revenue',
          data: chartVehicles.map((item) => Number(item.revenueAmount.toFixed(2)))
        }]
      }
    },
    async ensureVehicleLabels (vehicleIds) {
      const missingIds = vehicleIds.filter((id) => {
        if (!id) {
          return false
        }
        return !this.vehicleNameById[id] || !this.vehicleAgencyById[id]
      })
      if (!missingIds.length) {
        return
      }

      const profiles = await findTransportationProfiles({
        where: { id: { inq: missingIds } },
        fields: { id: true, licensePlateNumber: true, agencyId: true }
      })

      for (let i = 0; i < profiles.length; i += 1) {
        const profile = profiles[i]
        if (profile && profile.id) {
          this.vehicleNameById[profile.id] = profile.licensePlateNumber || profile.id
          if (profile.agencyId) {
            this.vehicleAgencyById[profile.id] = String(profile.agencyId)
          }
        }
      }

      for (let j = 0; j < missingIds.length; j += 1) {
        const id = missingIds[j]
        if (!this.vehicleNameById[id]) {
          this.vehicleNameById[id] = 'Vehicle ' + String(id).slice(0, 8)
        }
      }
    },
    vehicleLabel (vehicleId) {
      return this.vehicleNameById[vehicleId] || ('Vehicle ' + String(vehicleId).slice(0, 8))
    },
    formatDistance (distanceValue) {
      return Number(distanceValue || 0).toFixed(3) + ' km'
    },
    resolveCurrencySymbolForVehicleIds (vehicleIds) {
      const agencies = this.$store && this.$store.getters ? this.$store.getters.getAgencies : []
      const countryCurrencies = this.countryCurrenciesByCountryId || {}
      if (!Array.isArray(agencies)) {
        return ''
      }

      const agencyById = {}
      for (let i = 0; i < agencies.length; i += 1) {
        const agency = agencies[i]
        if (agency && agency.value) {
          agencyById[String(agency.value)] = agency
        }
      }

      const selectedVehicleIds = Array.isArray(vehicleIds) ? vehicleIds : []
      for (let i = 0; i < selectedVehicleIds.length; i += 1) {
        const vehicleId = selectedVehicleIds[i]
        const agencyId = this.vehicleAgencyById[vehicleId]
        if (!agencyId) {
          continue
        }

        const agency = agencyById[agencyId]
        const countryId = agency && agency.metadata ? agency.metadata.countryId : null
        if (!countryId) {
          continue
        }

        const countryCurrency = countryCurrencies[countryId]
        if (countryCurrency && countryCurrency.currencySymbol) {
          return String(countryCurrency.currencySymbol)
        }
      }

      return ''
    },
    formatCurrencyLabel (value, currencySymbol) {
      return formatCurrency(value, currencySymbol || '')
    },
    formatCurrencyAxisLabel (value) {
      return formatCurrency(value, '')
    },
    revenueChartOptions (card) {
      const self = this
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '70%'
          }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: card && card.vehicleLabels ? card.vehicleLabels : [],
          labels: {
            formatter: function (value) {
              return self.formatCurrencyAxisLabel(value)
            }
          }
        },
        yaxis: {
          title: {
            text: ''
          }
        },
        tooltip: {
          y: {
            formatter: function (value) {
              return self.formatCurrencyLabel(value, card && card.currencySymbol ? card.currencySymbol : '')
            }
          }
        }
      }
    }
  }
}
